import React, { useState } from 'react';
import {
  Mail, Phone, MessageSquare, Calendar, CheckSquare, FileText,
  ChevronDown, ChevronUp, Clock, User, ArrowUpRight, ArrowDownLeft,
  Pencil, Trash2, MoreVertical, HelpCircle
} from 'lucide-react';
import { Activity } from '../../types/activity';
import { TimeWithLocalHover } from '../common/TimeWithLocalHover';

interface ActivityCardProps {
  activity: Activity;
  currentUserId?: string;
  onEdit?: (activity: Activity) => void;
  onDelete?: (activity: Activity) => void;
}

const typeConfig = {
  email: { icon: Mail, color: 'text-blue-500', bgColor: 'bg-blue-50 dark:bg-blue-900/20' },
  call: { icon: Phone, color: 'text-green-500', bgColor: 'bg-green-50 dark:bg-green-900/20' },
  sms: { icon: MessageSquare, color: 'text-purple-500', bgColor: 'bg-purple-50 dark:bg-purple-900/20' },
  meeting: { icon: Calendar, color: 'text-orange-500', bgColor: 'bg-orange-50 dark:bg-orange-900/20' },
  task: { icon: CheckSquare, color: 'text-teal-500', bgColor: 'bg-teal-50 dark:bg-teal-900/20' },
  note: { icon: FileText, color: 'text-gray-500', bgColor: 'bg-gray-50 dark:bg-gray-900/20' },
};


/**
 * Format meeting time with timezone abbreviation (e.g., "2:00 PM ET")
 */
const formatMeetingTime = (dateString: string, timezone?: string): string => {
  const date = new Date(dateString);

  // Format time in the original timezone
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: timezone,
  };

  // Get timezone abbreviation
  const tzOptions: Intl.DateTimeFormatOptions = {
    timeZoneName: 'short',
    timeZone: timezone,
  };

  const timeStr = date.toLocaleTimeString('en-US', timeOptions);
  const tzStr = date.toLocaleString('en-US', tzOptions).split(' ').pop() || '';

  return `${timeStr} ${tzStr}`;
};

/**
 * Format meeting date (e.g., "Feb 26, 2026")
 */
const formatMeetingDate = (dateString: string, timezone?: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: timezone,
  });
};

/**
 * Get local time equivalent for tooltip
 */
const getLocalTimeString = (dateString: string): string => {
  const date = new Date(dateString);
  const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  const tzStr = date.toLocaleString('en-US', { timeZoneName: 'short' }).split(' ').pop() || '';

  return `${timeStr} ${tzStr} (your time)`;
};

export const ActivityCard: React.FC<ActivityCardProps> = ({
  activity,
  currentUserId,
  onEdit,
  onDelete,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const config = typeConfig[activity.type];
  const Icon = config.icon;

  // Only show edit/delete if user owns the activity (or if currentUserId not provided, show for all)
  const canModify = !currentUserId || activity.userId === currentUserId;
  const hasActions = canModify && (onEdit || onDelete);

  return (
    <div className={`rounded-lg border border-gray-200 dark:border-gray-700 ${config.bgColor} p-4`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-full ${config.bgColor}`}>
            <Icon className={`w-5 h-5 ${config.color}`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900 dark:text-white">
                {activity.type.charAt(0).toUpperCase() + activity.type.slice(1)}
                {activity.subject && `: ${activity.subject}`}
              </span>
              {activity.direction && (
                activity.direction === 'outbound'
                  ? <ArrowUpRight className="w-4 h-4 text-gray-400" />
                  : <ArrowDownLeft className="w-4 h-4 text-gray-400" />
              )}
            </div>

            {/* Quick info line */}
            <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-gray-500 dark:text-gray-400">
              {/* Meeting scheduled time with timezone hover */}
              {activity.type === 'meeting' && activity.scheduledAt && (
                <span
                  className="flex items-center gap-1 cursor-help group relative"
                  title={getLocalTimeString(activity.scheduledAt)}
                >
                  <Calendar className="w-3 h-3" />
                  <span>{formatMeetingDate(activity.scheduledAt, activity.timezone)}</span>
                  <span>at</span>
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    {formatMeetingTime(activity.scheduledAt, activity.timezone)}
                  </span>
                  {/* Tooltip */}
                  <span className="absolute bottom-full left-0 mb-1 px-2 py-1 text-xs bg-gray-900 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                    {getLocalTimeString(activity.scheduledAt)}
                  </span>
                </span>
              )}
              {/* Meeting location */}
              {activity.type === 'meeting' && activity.location && (
                <span className="truncate max-w-[200px]" title={activity.location}>
                  {activity.location}
                </span>
              )}
              {activity.recipientEmail && (
                <span>To: {activity.recipientEmail}</span>
              )}
              {activity.recipientPhone && (
                <span>To: {activity.recipientPhone}</span>
              )}
              {activity.duration && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {activity.duration} mins
                </span>
              )}
              {activity.outcome && (
                <span className="capitalize">{activity.outcome.replace('_', ' ')}</span>
              )}
            </div>

            {/* User and time */}
            <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
              <User className="w-3 h-3" />
              <span>{activity.userName}</span>
              <span>•</span>
              <TimeWithLocalHover
                datetime={activity.createdAt}
                timezone={activity.timezone}
              />
              {activity.templateName && (
                <>
                  <span>•</span>
                  <span>Template: {activity.templateName}</span>
                </>
              )}
            </div>

            {/* Expanded details */}
            {expanded && activity.description && (
              <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                  {activity.description}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          {/* Expand button */}
          {activity.description && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
            >
              {expanded ? (
                <ChevronUp className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              )}
            </button>
          )}

          {/* Edit/Delete actions menu */}
          {hasActions && (
            <div className="relative">
              <button
                onClick={() => setShowActions(!showActions)}
                className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
              >
                <MoreVertical className="w-4 h-4 text-gray-400" />
              </button>

              {showActions && (
                <>
                  {/* Backdrop to close menu */}
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowActions(false)}
                  />
                  {/* Menu */}
                  <div className="absolute right-0 mt-1 w-32 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-20">
                    {onEdit && (
                      <button
                        onClick={() => {
                          setShowActions(false);
                          onEdit(activity);
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 first:rounded-t-md"
                      >
                        <Pencil className="w-4 h-4" />
                        Edit
                      </button>
                    )}
                    {onDelete && (
                      <button
                        onClick={() => {
                          setShowActions(false);
                          onDelete(activity);
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 last:rounded-b-md"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
